// Modal Component

/**
 * Create a modal dialog
 * @param {string} id - Modal ID
 * @param {string} title - Modal title
 * @param {string} content - Modal body content
 * @param {Object} options - Additional options
 * @returns {string} HTML string
 */
export function createModal(id, title, content, options = {}) {
    const {
        showFooter = true,
        confirmText = 'Confirm',
        cancelText = 'Cancel',
        size = 'default' // 'default', 'large', 'small'
    } = options;

    const sizeClass = size === 'large' ? 'modal-lg' : size === 'small' ? 'modal-sm' : '';

    return `
    <div class="modal-backdrop" id="${id}">
      <div class="modal ${sizeClass}" role="dialog" aria-labelledby="${id}-title">
        <div class="modal-header">
          <h3 class="modal-title" id="${id}-title">${title}</h3>
          <button class="modal-close" data-modal-close aria-label="Close modal">
            ✕
          </button>
        </div>
        <div class="modal-body">
          ${content}
        </div>
        ${showFooter ? `
          <div class="modal-footer">
            <button class="btn btn-secondary" data-modal-close>${cancelText}</button>
            <button class="btn btn-primary" data-modal-confirm>${confirmText}</button>
          </div>
        ` : ''}
      </div>
    </div>
  `;
}

/**
 * Show a modal by ID
 * @param {string} id - Modal ID
 */
export function showModal(id) {
    const modal = document.getElementById(id);
    if (modal) {
        modal.classList.add('active');
        document.body.style.overflow = 'hidden';

        // Focus trap
        const firstFocusable = modal.querySelector('button, input, select, textarea');
        if (firstFocusable) firstFocusable.focus();
    }
}

/**
 * Hide a modal by ID
 * @param {string} id - Modal ID
 */
export function hideModal(id) {
    const modal = document.getElementById(id);
    if (modal) {
        modal.classList.remove('active');
        document.body.style.overflow = '';
    }
}

/**
 * Initialize modal event listeners
 * Call this after rendering modals to the DOM
 */
export function initModals() {
    // Close button clicks
    document.querySelectorAll('[data-modal-close]').forEach(btn => {
        btn.addEventListener('click', (e) => {
            const modal = e.target.closest('.modal-backdrop');
            if (modal) {
                hideModal(modal.id);
            }
        });
    });

    // Backdrop clicks
    document.querySelectorAll('.modal-backdrop').forEach(backdrop => {
        backdrop.addEventListener('click', (e) => {
            if (e.target === backdrop) {
                hideModal(backdrop.id);
            }
        });
    });

    // Escape key
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
            const activeModal = document.querySelector('.modal-backdrop.active');
            if (activeModal) {
                hideModal(activeModal.id);
            }
        }
    });
}

/**
 * Create and show a confirmation dialog
 * @param {string} title - Dialog title
 * @param {string} message - Dialog message
 * @returns {Promise<boolean>} True if confirmed
 */
export function confirm(title, message) {
    return new Promise((resolve) => {
        const id = 'confirm-dialog-' + Date.now();
        const html = createModal(id, title, `<p>${message}</p>`, {
            confirmText: 'Yes',
            cancelText: 'No'
        });

        // Append to body
        const container = document.createElement('div');
        container.innerHTML = html;
        document.body.appendChild(container.firstElementChild);

        const modal = document.getElementById(id);

        // Set up handlers
        modal.querySelector('[data-modal-confirm]').addEventListener('click', () => {
            hideModal(id);
            setTimeout(() => modal.remove(), 300);
            resolve(true);
        });

        modal.querySelector('[data-modal-close]').addEventListener('click', () => {
            hideModal(id);
            setTimeout(() => modal.remove(), 300);
            resolve(false);
        });

        // Backdrop click
        modal.addEventListener('click', (e) => {
            if (e.target === modal) {
                hideModal(id);
                setTimeout(() => modal.remove(), 300);
                resolve(false);
            }
        });

        // Show modal
        requestAnimationFrame(() => showModal(id));
    });
}
